import type { ViewIndependentData } from '@lighthouse/core'
import { WritableDraft } from 'immer/dist/internal'
import { WritableAtom } from 'jotai'
import { atomFamily } from 'jotai/utils'
import { AtomFamily } from 'jotai/vanilla/utils/atomFamily'
import { atomWithImmer } from 'jotai-immer'
import React, { createContext, useContext, useMemo } from 'react'

import type { RecordPoolMap } from '../utils/recordPool'

type FieldBlockContextValue = {
    appId: string
    stackId: string
    /** recordPoolAtom 使用 Map 结构，key 为 `${dsId}-${recordId}` */
    recordPoolAtom: ReturnType<typeof atomWithImmer<RecordPoolMap>>
    viewIndependentDataAtomFamily: AtomFamily<
        {
            stackId: string
            viewId: string
            dsId: string
        },
        WritableAtom<ViewIndependentData, [ViewIndependentData | ((draft: WritableDraft<ViewIndependentData>) => void)], void>
    >
}

interface FieldBlockProviderProps extends FieldBlockContextValue {
    children?: React.ReactNode
}

export const FieldBlockContext = createContext<FieldBlockContextValue>({
    appId: '',
    stackId: '',
    recordPoolAtom: atomWithImmer<RecordPoolMap>(new Map()),
    viewIndependentDataAtomFamily: atomFamily((payload: { stackId: string; viewId: string; dsId: string }) => {
        return atomWithImmer<ViewIndependentData>({
            dsId: payload.dsId,
            recordPoints: [],
            currentPage: 1,
            rowTotal: 0,
            search: ''
        })
    })
})

export const FieldBlockProvider: React.FC<FieldBlockProviderProps> = ({
    appId,
    stackId,
    recordPoolAtom,
    viewIndependentDataAtomFamily,
    children
}) => {
    const value = useMemo(
        () => ({ appId, stackId, recordPoolAtom, viewIndependentDataAtomFamily }),
        [appId, recordPoolAtom, stackId, viewIndependentDataAtomFamily]
    )

    return <FieldBlockContext.Provider value={value}>{children}</FieldBlockContext.Provider>
}

export const useFieldBlockContext = () => useContext(FieldBlockContext)
